import classNames from 'classnames'
import { observer } from 'mobx-react-lite'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { dmsState } from '@/app/store'
import { type IUpdateInsuredDto, type ICreateInsuredDto } from '@/app/types/dmsTypes'
import { common, onCheckDisabledConditions } from '@/app/utils'
import { Loader } from '@/shared'
import { routes } from '@Config/config'

import { onHandleNullableDtoParam, type IDmsActionType } from '../../../helper'
import { AutoCompleteInsuranceCompany } from '../../AutoCompleteInsuranceCompany/AutoCompleteInsuranceCompany'
import { DateField } from '../../DateField/DateField'
import { RegularField } from '../../RegularField/RegularField'
import { RemoveModal } from '../../RemoveModal/RemoveModal'
import { SelectColored } from '../../SelectColored/SelectColored'
import { TelephoneField } from '../../TelephoneField/TelephoneField'

import style from './Data.module.scss'
import { InsuredContracts } from './InsuredContracts/InsuredContracts'
import { useFields } from './useFields'

export const Data = observer(() => {
    const navi = useNavigate()

    if (!dmsState.chosenInsured) {
        return <Loader />
    }

    const type = Object.keys(dmsState.chosenInsured)[0]

    const [isRemovablePatient, setIsRemovablePatient] = useState<boolean>(false)

    const {
        iname,
        onChangeIname,
        onSetIsTouchedIname,
        fname,
        onChangeFname,
        onSetIsTouchedFname,
        oname,
        onChangeOname,
        onSetIsTouchedOname,
        polisNumber,
        onChangePolisNumber,
        onSetIsTouchedPolisNumber,
        birthDate,
        onChangeBirthDate,
        onSetIsTouchedBirthDate,
        expirationDate,
        onChangeExpirationDate,
        onSetIsTouchedExpirationDate,
        polisStartedAt,
        onChangeStartDate,
        polisFinishedAt,
        onChangeEndDate,
        insuranceCompanyId,
        onChangeInsuranceCompanyId,
        onSetIsTouchedInsuranceCompanyId,
        address,
        onChangeAddress,
        onSetIsTouchedAddress,
        insurer,
        onChangeInsurer,
        onSetIsTouchedInsurer,
        marker,
        onAddMarker,
        onRemoveMarker,
        phone,
        onChangePhone,
        onSetIsTouchedPhone,
        isSomeToched,
        onSetTouchAll
    } = useFields({ type, data: dmsState.chosenInsured[type], markersList: dmsState.guideMarkers })

    const creactingDto: ICreateInsuredDto = {
        insuranceCompanyId: insuranceCompanyId.value || '',
        fname: fname.value || '',
        iname: iname.value || '',
        oname: onHandleNullableDtoParam(oname.value),
        birthDate: birthDate.value || '',
        expirationDate: expirationDate.value || '',
        address: onHandleNullableDtoParam(address.value),
        phone: phone.value ? common.onGetPhoneBackFormat(phone.value) : '',
        polisNumber: polisNumber.value || '',
        insurer: insurer.value || '',
        identificationMarkers: marker.value ? marker.value.split(',') : []
    }

    const updatingDto: IUpdateInsuredDto = {
        polisId: dmsState.chosenInsured[type].polisId,
        fname: fname.value || '',
        iname: iname.value || '',
        oname: onHandleNullableDtoParam(oname.value),
        birthDate: birthDate.value || '',
        expirationDate: expirationDate.value || '',
        address: onHandleNullableDtoParam(address.value),
        phone: phone.value ? common.onGetPhoneBackFormat(phone.value) : '',
        polisNumber: polisNumber.value || '',
        insurer: insurer.value || '',
        identificationMarkers: marker.value ? marker.value.split(',') : []
    }

    const createPatientAction: IDmsActionType = {
        title: 'Сохранить',
        type: 'create',
        callback: () =>
            dmsState.onAddInsured(creactingDto, (newInsuredId: string) => {
                dmsState.onSetChosenInsured(null)
                navi(`${routes.DMSM}/insureds/${newInsuredId}`)
            }),
        isDisabledRequired: true
    }

    const saveChangesAction: IDmsActionType = {
        title: 'Сохранить изменения',
        type: 'update',
        callback: () => {
            if (dmsState.chosenInsured) {
                dmsState.onSaveInsuredChanges(
                    updatingDto,
                    dmsState.chosenInsured[type].incuranceCompanyId,
                    (newInsuredId: string) => {
                        navi(`${routes.DMSM}/insureds/${newInsuredId}`)
                    }
                )
            }
        },
        isDisabledRequired: true
    }

    const removePatientAction: IDmsActionType = {
        title: 'Удалить пациента',
        type: 'remove',
        callback: () => setIsRemovablePatient(true),
        isDisabledRequired: false
    }

    const onRemoveInsuredHandler = () => {
        if (dmsState.chosenInsured?.[type]?.polisId) {
            dmsState.onRemoveInsured(
                dmsState.chosenInsured[type].polisId,
                dmsState.chosenInsured[type].incuranceCompanyId,
                () => {
                    dmsState.onSetChosenInsured(null)
                    setIsRemovablePatient(false)
                    navi(`${routes.DMSM}/insureds`)
                }
            )
        }
    }

    const actions: Record<string, IDmsActionType[]> = {
        add: [createPatientAction],
        edit: [saveChangesAction, removePatientAction]
    }

    const isValidFields = onCheckDisabledConditions(
        Boolean(!iname.error),
        Boolean(!fname.error),
        Boolean(!birthDate.error),
        Boolean(!polisNumber.error),
        Boolean(!phone.error),
        Boolean(!insurer.error),
        Boolean(!insuranceCompanyId.error)
    )

    return (
        <div className={style.container}>
            <div className={style.singleField}>
                <AutoCompleteInsuranceCompany
                    entity={insuranceCompanyId}
                    isDisabled={type === 'edit'}
                    onSetInsuranceCompanyId={onChangeInsuranceCompanyId}
                    onTouch={onSetIsTouchedInsuranceCompanyId}
                    isNotificator={type === 'edit'}
                />
            </div>
            {type === 'edit' && (
                <div className={style.doubleFieldField}>
                    <DateField entity={polisStartedAt} onChange={onChangeStartDate} disabled isNotificator />
                    <DateField entity={polisFinishedAt} onChange={onChangeEndDate} disabled isNotificator />
                </div>
            )}
            <div className={style.tripleFieldField}>
                <RegularField
                    entity={fname}
                    onChange={onChangeFname}
                    onSetIsTouched={onSetIsTouchedFname}
                    disabled={false}
                />
                <RegularField
                    entity={iname}
                    onChange={onChangeIname}
                    onSetIsTouched={onSetIsTouchedIname}
                    disabled={false}
                />
                <RegularField
                    entity={oname}
                    onChange={onChangeOname}
                    onSetIsTouched={onSetIsTouchedOname}
                    disabled={false}
                />
            </div>
            <div className={style.tripleFieldField}>
                <DateField
                    entity={birthDate}
                    onChange={onChangeBirthDate}
                    onSetIsTouched={onSetIsTouchedBirthDate}
                    disabled={false}
                />
                <DateField
                    entity={expirationDate}
                    onChange={onChangeExpirationDate}
                    onSetIsTouched={onSetIsTouchedExpirationDate}
                    disabled={false}
                />
                <RegularField
                    entity={polisNumber}
                    onChange={onChangePolisNumber}
                    onSetIsTouched={onSetIsTouchedPolisNumber}
                    disabled={false}
                />
            </div>
            <div className={style.quadrupleField}>
                <RegularField
                    entity={insurer}
                    onChange={onChangeInsurer}
                    onSetIsTouched={onSetIsTouchedInsurer}
                    disabled={false}
                />
                <TelephoneField
                    entity={phone}
                    onChange={onChangePhone}
                    onSetIsTouched={onSetIsTouchedPhone}
                    disabled={false}
                />
                <RegularField
                    entity={address}
                    onChange={onChangeAddress}
                    onSetIsTouched={onSetIsTouchedAddress}
                    disabled={false}
                />
                <SelectColored
                    entity={marker}
                    onAddMarker={onAddMarker}
                    onRemoveMarker={onRemoveMarker}
                    onSetIsTouched={() => {}}
                    options={dmsState.guideMarkers}
                />
            </div>
            <div className={style.actions}>
                {actions[type].map(({ title, type, callback, isDisabledRequired }) => {
                    const onClick = () => {
                        if (!isValidFields || isSomeToched || !isDisabledRequired) {
                            callback()
                        } else {
                            onSetTouchAll()
                        }
                    }
                    const onSetDisabledMode = (): boolean => {
                        if (isDisabledRequired) {
                            if (!isSomeToched) {
                                return false
                            }
                            if (!isValidFields) {
                                return false
                            }
                            return true
                        }
                        return false
                    }
                    return (
                        <button
                            key={title}
                            className={classNames(style.btn, style[type])}
                            onClick={onClick}
                            disabled={onSetDisabledMode()}
                        >
                            {title}
                        </button>
                    )
                })}
            </div>
            {insuranceCompanyId.value && <InsuredContracts type={type} companyId={insuranceCompanyId.value} />}
            {isRemovablePatient && (
                <RemoveModal
                    entity={common.fioHandler(
                        {
                            fname: dmsState.chosenInsured[type].fname,
                            iname: dmsState.chosenInsured[type].iname,
                            oname: dmsState.chosenInsured[type].oname
                        },
                        'fullname'
                    )}
                    headerTitle='Вы уверены, что хотите удалить застрахованного?'
                    onClose={() => setIsRemovablePatient(false)}
                    onRemoveCallback={onRemoveInsuredHandler}
                />
            )}
        </div>
    )
})
